/**
 * Écran de compte : identité, profil, préférences, sessions actives.
 *
 * Déplacé de `features/auth` en phase 2 : gérer son compte n'est pas
 * s'authentifier, et l'écran a maintenant trois formulaires qui n'ont rien à
 * voir avec la connexion. Les hooks de session, eux, restent dans `auth`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useAccount, useProfile, useSettings } from "@/features/account/api/useAccount";
import { DangerZone } from "@/features/account/components/DangerZone";
import { IdentityForm } from "@/features/account/components/IdentityForm";
import { PreferencesForm } from "@/features/account/components/PreferencesForm";
import { ProfileForm } from "@/features/account/components/ProfileForm";
import { useLogout, useRevokeSession, useSessions } from "@/features/auth/api/useAuth";
import { useApiErrorMessage } from "@/features/auth/lib/useApiErrorMessage";
import { authKeys } from "@/shared/api/query-keys";
import { useSessionStore } from "@/shared/auth/session";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function AccountPage() {
  const { t, i18n } = useTranslation("account");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useSessionStore((state) => state.user);
  const account = useAccount();
  const profile = useProfile();
  const settings = useSettings();
  const sessions = useSessions();
  const revoke = useRevokeSession();
  const logout = useLogout();
  const errorMessage = useApiErrorMessage();

  // L'écriture demande un email confirmé (docs/06 §4) : le serveur le refuse de
  // toute façon, mais griser les champs vaut mieux que laisser saisir dix
  // lignes de biographie pour finir sur un 403.
  const canEdit = account.data?.is_email_verified ?? false;

  const formatDate = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  const handleLogout = () => {
    logout.mutate(undefined, {
      // `onSettled` : la session locale est purgée par le hook quoi qu'il
      // arrive, la redirection doit suivre le même sort.
      onSettled: () => {
        void navigate({ to: "/login" });
      },
    });
  };

  const handleRevoke = (sessionId: string) => {
    revoke.mutate(sessionId, {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: authKeys.sessions() });
      },
    });
  };

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold tracking-tight">{t("heading")}</h1>
        <Button variant="secondary" onClick={handleLogout}>
          {t("logout")}
        </Button>
      </header>

      {account.isPending && <Skeleton className="h-4 w-2/3" label={t("loading")} />}
      {account.isError && (
        <ErrorState error={account.error} onRetry={() => void account.refetch()} />
      )}

      {account.data !== undefined && !canEdit && (
        <Alert tone="info">
          {t("unverifiedBanner")} {t("readOnlyNotice")}
        </Alert>
      )}

      {/* En tête d'écran, et non dans la carte du bas : une suppression
          programmée est l'information la plus importante de la page, et la
          laisser en pied ferait modifier un profil qui n'existera plus. */}
      {account.data?.deletion_scheduled_for !== undefined &&
        account.data.deletion_scheduled_for !== null && (
          <Alert tone="warning">
            {t("privacy.deletion.scheduled", {
              date: formatDate(account.data.deletion_scheduled_for),
            })}
          </Alert>
        )}

      {account.data !== undefined && (
        <IdentityForm
          account={account.data}
          canEdit={canEdit}
          roles={user?.roles ?? []}
          permissionCount={user?.permissions.length ?? 0}
        />
      )}

      {profile.data !== undefined && <ProfileForm profile={profile.data} canEdit={canEdit} />}
      {settings.data !== undefined && (
        <PreferencesForm settings={settings.data} canEdit={canEdit} />
      )}

      {account.data !== undefined && <DangerZone account={account.data} formatDate={formatDate} />}

      <Card title={t("sessions.heading")}>
        {sessions.isPending && <Skeleton className="h-4 w-2/3" label={t("sessions.loading")} />}
        {sessions.isError && (
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        )}
        {revoke.isError && <Alert tone="error">{errorMessage(revoke.error)}</Alert>}

        {sessions.data !== undefined && (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {sessions.data.map((session) => (
              <li key={session.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {session.user_agent ?? t("sessions.unknownDevice")}
                    {session.is_current && (
                      <span className="text-brand-600 ml-2 text-xs">{t("sessions.current")}</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t("sessions.lastUsed", { date: formatDate(session.last_used_at) })}
                    {session.ip_address !== null && ` · ${session.ip_address}`}
                  </p>
                </div>
                {!session.is_current && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      handleRevoke(session.id);
                    }}
                    disabled={revoke.isPending}
                  >
                    {t("sessions.revoke")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
